/**
 * ResultsTable.tsx — ranked candidates as a DataGrid (ported from the Juicebox replica's EsResultsTable).
 *
 * Columns: Name · Profiles · Job Title · Company · Location · then either Match% + one 3-state verdict
 * column per criterion (scored), or a "Why matched" evidence column (unscored). Clicking a row expands
 * a per-criterion detail panel; clicking the name opens the profile drawer. Reads the crypto-lever
 * `_source` candidate shape; criteria verdicts/labels/match_rate come from apps/server's Gemini scoring.
 */

import { Fragment } from 'react';
import { ThumbsUp, Ban, Download } from 'lucide-react';
import type { CandidateResult, CriteriaResult } from '@/lib/talentPool.api';
import { CompanyLogo } from '@/app/agent/[id]/artifacts/types/search-strategy/primitives';
import { LinkedinIcon, GithubIcon } from './brandIcons';

const TH =
    'h-9 text-left font-semibold text-[12px] text-[var(--text-tertiary)] bg-[var(--surface-secondary)] border-b border-[var(--border-muted)] px-3 sticky top-0 z-[1] whitespace-nowrap';
const TD = 'px-3 align-middle border-b border-[var(--border-muted)]';
const clip = 'block whitespace-nowrap overflow-hidden text-ellipsis';

const activeCompany = (c: CandidateResult) =>
    c.experience?.find((e) => e.active_experience === 1)?.company_name || c.experience?.[0]?.company_name || '';
const activeDomain = (c: CandidateResult) =>
    c.experience?.find((e) => e.active_experience === 1)?.company_domain || c.experience?.[0]?.company_domain;
const favicon = (domain?: string) => (domain ? `https://www.google.com/s2/favicons?domain=${domain}&sz=64` : undefined);
const locationOf = (c: CandidateResult) => c.location_city_canonical || c.location_city || c.location_full || '';

const VERDICT: Record<CriteriaResult['verdict'], { Icon: typeof ThumbsUp; color: string }> = {
    yes: { Icon: ThumbsUp, color: '#2e8b4f' },
    maybe: { Icon: ThumbsUp, color: '#d4a84b' },
    no: { Icon: Ban, color: '#d14b4b' },
};

function Verdict({ v }: { v: CriteriaResult['verdict'] }) {
    const { Icon, color } = VERDICT[v];
    return <Icon size={15} style={{ color }} className="inline-block" />;
}

/**
 * The role the query actually MATCHED, when it isn't the candidate's current one — e.g. a `when:"any"`
 * title hit on a past role. Without this, an exact match on "Data Scientist (2019)" renders as a row
 * titled "First Vice-President" and reads as a bad result. Evidence strings come from the engine's
 * inner_hits ("Title @ Company"); skill/language/cert entries are prefixed and skipped, and a role that's
 * already reflected in the displayed title is suppressed as noise.
 */
function matchedRole(r: CandidateResult): string | null {
    const displayed = (r.headline || r.active_experience_title || '').toLowerCase();
    for (const ev of r._evidence ?? []) {
        if (/^(skill|language|cert):/i.test(ev)) continue;
        const role = ev.split(' @ ')[0]?.trim();
        if (!role) continue;
        if (displayed.includes(role.toLowerCase())) return null; // matched the current role — nothing to explain
        return ev;
    }
    return null;
}

/** RFC-4180 CSV cell: quote when needed, double inner quotes. */
const csvCell = (v: unknown) => {
    const s = v == null ? '' : String(v);
    return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

/** Download the loaded rows as CSV — core columns, then Match% + one verdict column per criterion when scored. */
function exportCsv(rows: CandidateResult[], criteria: string[], labels: string[], scored: boolean) {
    const critHeads = scored ? criteria.map((c, i) => labels[i] || c) : [];
    const head = [
        'Name', 'Job Title', 'Company', 'Location', 'Email', 'LinkedIn', 'GitHub',
        ...(scored ? ['Match %', 'Criteria Met'] : []),
        ...critHeads,
        'Matched Role', 'Criteria Summary',
    ];
    const lines = rows.map((r) =>
        [
            r.full_name ?? '',
            r.headline || r.active_experience_title || '',
            activeCompany(r),
            locationOf(r),
            r.emails?.[0] ?? '',
            r.linkedin_url ?? '',
            r.github_url ?? '',
            ...(scored ? [r.match_rate ?? '', r.criteria_total != null ? `${r.criteria_met ?? 0}/${r.criteria_total}` : ''] : []),
            ...(scored ? criteria.map((_, i) => r.criteria_results?.[i]?.verdict ?? '') : []),
            matchedRole(r) ?? '',
            r.criteria_summary ?? '',
        ].map(csvCell).join(','),
    );
    const csv = '\ufeff' + [head.map(csvCell).join(','), ...lines].join('\r\n'); // BOM so Excel opens UTF-8 names correctly
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `talent-pool-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
}

export function ResultsTable({
    rows,
    total,
    criteria,
    criteriaLabels,
    scored,
    scoring = false,
    expandedId,
    onToggleExpand,
    onOpen,
}: {
    rows: CandidateResult[];
    total: number;
    criteria: string[];
    criteriaLabels?: string[];
    scored: boolean;
    scoring?: boolean; // async scoring in flight — rows without criteria_results yet show a pending cell
    expandedId: string | null;
    onToggleExpand: (id: string) => void;
    onOpen: (c: CandidateResult) => void;
}) {
    const showCriteria = scored && criteria.length > 0;
    const labels = criteriaLabels && criteriaLabels.length === criteria.length ? criteriaLabels : criteria.map(() => '');
    const colSpan = showCriteria ? 7 + criteria.length : 7;

    return (
        <div className="overflow-hidden rounded-lg border border-[var(--border-muted)] bg-[var(--surface-primary)]">
            <div className="flex h-11 items-center justify-between border-b border-[var(--border-muted)] px-3">
                <span className="text-[14px] font-semibold text-[var(--text-primary)]">Matches ({total.toLocaleString()})</span>
                {rows.length > 0 && (
                    <button
                        type="button"
                        onClick={() => exportCsv(rows, criteria, labels, showCriteria)}
                        className="inline-flex items-center gap-1.5 rounded-md border border-[var(--border-muted)] px-2.5 py-1.5 text-[12px] font-medium text-[var(--text-secondary)] transition-colors hover:bg-[var(--surface-secondary)] hover:text-[var(--text-primary)]"
                        title={`Download the ${rows.length} loaded row${rows.length === 1 ? '' : 's'} as CSV`}
                    >
                        <Download size={13} /> Export CSV
                    </button>
                )}
            </div>

            <div className="overflow-x-auto">
                <table className="w-max min-w-full border-collapse text-[13px]">
                    <thead>
                        <tr>
                            <th className={TH} style={{ width: 210 }}>
                                Name
                            </th>
                            <th className={TH} style={{ width: 76 }}>
                                Profiles
                            </th>
                            <th className={TH} style={{ width: 200 }}>
                                Job Title
                            </th>
                            <th className={TH} style={{ width: 180 }}>
                                Company
                            </th>
                            <th className={TH} style={{ width: 150 }}>
                                Location
                            </th>
                            <th className={TH} style={{ width: 200 }}>
                                Email
                            </th>
                            {showCriteria ? (
                                <>
                                    <th
                                        className={`${TH} !text-center`}
                                        style={{ width: 80 }}
                                        title="Combined score across all criteria, in order of importance."
                                    >
                                        Match
                                    </th>
                                    {criteria.map((cr, i) => (
                                        <th key={i} className={`${TH} !text-center`} style={{ width: 116 }} title={cr}>
                                            {labels[i] || `C${i + 1}`}
                                        </th>
                                    ))}
                                </>
                            ) : (
                                <th className={TH} style={{ width: 360 }}>
                                    Why matched
                                </th>
                            )}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r) => {
                            const isExp = expandedId === r.id;
                            const results = r.criteria_results || [];
                            const rowUnscored = results.length === 0; // no criteria scored for this row (pending or never-scored)
                            const skills = (r.skills?.map((s) => s.skill) ?? r.skills_canonical ?? r.inferred_skills ?? []).filter(Boolean);
                            return (
                                <Fragment key={r.id}>
                                    <tr
                                        onClick={() => onToggleExpand(r.id)}
                                        className={`h-[38px] cursor-pointer ${isExp ? 'bg-[var(--surface-secondary)]' : 'hover:bg-[var(--surface-secondary)]'}`}
                                    >
                                        {/* Name */}
                                        <td className={TD}>
                                            <span className="inline-flex items-center" onClick={(e) => e.stopPropagation()}>
                                                <button
                                                    type="button"
                                                    onClick={() => onOpen(r)}
                                                    className="max-w-[180px] overflow-hidden text-ellipsis whitespace-nowrap text-left text-[13px] text-[var(--text-primary)] hover:text-[#0d1830] hover:underline"
                                                >
                                                    {r.full_name}
                                                </button>
                                            </span>
                                        </td>
                                        {/* Profiles */}
                                        <td className={TD} onClick={(e) => e.stopPropagation()}>
                                            <span className="inline-flex items-center gap-2.5">
                                                {r.linkedin_url && (
                                                    <a href={r.linkedin_url} target="_blank" rel="noreferrer" className="opacity-90 hover:opacity-100">
                                                        <LinkedinIcon size={16} />
                                                    </a>
                                                )}
                                                {r.github_url && (
                                                    <a href={r.github_url} target="_blank" rel="noreferrer" className="opacity-90 hover:opacity-100">
                                                        <GithubIcon size={16} />
                                                    </a>
                                                )}
                                            </span>
                                        </td>
                                        {/* Job Title (+ the matched past role when it isn't the current one) */}
                                        <td className={`${TD} max-w-[400px]`}>
                                            <span className={`${clip} max-w-[400px]`} title={r.headline ?? undefined}>
                                                {r.headline || r.active_experience_title}
                                            </span>
                                            {matchedRole(r) && (
                                                <span
                                                    className={`${clip} mt-0.5 block max-w-[400px] text-[11px] text-[var(--text-tertiary)]`}
                                                    title={`This candidate matched your filters through this role: ${matchedRole(r)}`}
                                                >
                                                    matched: {matchedRole(r)}
                                                </span>
                                            )}
                                        </td>
                                        {/* Company */}
                                        <td className={`${TD} max-w-[150px]`}>
                                            <span className="inline-flex min-w-0 max-w-full items-center gap-2">
                                                <CompanyLogo name={activeCompany(r)} src={favicon(activeDomain(r))} size={18} />
                                                <span className={`${clip} max-w-[120px]`} title={activeCompany(r) ?? undefined}>{activeCompany(r)}</span>
                                            </span>
                                        </td>
                                        {/* Location */}
                                        <td className={TD}>
                                            <span className={`${clip} text-[var(--text-secondary)]`}>{locationOf(r)}</span>
                                        </td>
                                        {/* Email */}
                                        <td className={`${TD} max-w-[200px]`} onClick={(e) => e.stopPropagation()}>
                                            {r.emails?.[0] ? (
                                                <a
                                                    href={`mailto:${r.emails[0]}`}
                                                    title={r.emails.join(', ')}
                                                    className={`${clip} max-w-[200px] text-[var(--text-secondary)] hover:text-[#0d1830] hover:underline`}
                                                >
                                                    {r.emails[0]}
                                                </a>
                                            ) : (
                                                <span className="text-[var(--text-muted)]">—</span>
                                            )}
                                        </td>
                                        {showCriteria ? (
                                            <>
                                                <td className={`${TD} text-center`}>
                                                    {r.match_rate != null ? (
                                                        <span className="text-[13px] font-medium text-[var(--text-primary)]">{r.match_rate}%</span>
                                                    ) : rowUnscored && scoring ? (
                                                        <span className="inline-block h-3 w-3 animate-pulse rounded-full bg-[var(--border-muted)]" title="scoring…" />
                                                    ) : (
                                                        <span className="text-[13px] text-[var(--text-muted)]" title={rowUnscored ? 'not scored' : undefined}>—</span>
                                                    )}
                                                </td>
                                                {criteria.map((cr, i) => {
                                                    const res = results[i];
                                                    // never show a red "no" for a row that was NOT scored — only for genuinely-scored rows
                                                    return (
                                                        <td
                                                            key={i}
                                                            className={`${TD} text-center`}
                                                            title={rowUnscored ? (scoring ? 'scoring…' : 'not scored') : `${cr}${res?.explanation ? ` — ${res.explanation}` : ''}`}
                                                        >
                                                            {rowUnscored ? <span className="text-[var(--text-muted)]">·</span> : <Verdict v={res?.verdict ?? 'no'} />}
                                                        </td>
                                                    );
                                                })}
                                            </>
                                        ) : (
                                            <td className={TD}>
                                                <span className={`${clip} text-[12.5px] text-[var(--text-tertiary)]`}>{(r._evidence || []).join(' · ')}</span>
                                            </td>
                                        )}
                                    </tr>

                                    {isExp && (
                                        <tr className="bg-[var(--surface-secondary)]">
                                            <td className="border-b border-[var(--border-muted)] p-0" colSpan={colSpan}>
                                                <div className="py-3.5 pl-11 pr-6">
                                                    {showCriteria ? (
                                                        <div className="flex flex-wrap gap-x-8 gap-y-3">
                                                            {criteria.map((cr, i) => {
                                                                const res = results[i];
                                                                return (
                                                                    <div key={i} className="min-w-[230px] max-w-[320px] flex-1">
                                                                        <div className="mb-1 flex items-center gap-1.5">
                                                                            <Verdict v={res?.verdict ?? 'no'} />
                                                                            <span className="text-[13px] font-medium text-[var(--text-primary)]">{labels[i] || `C${i + 1}`}</span>
                                                                        </div>
                                                                        <p className="m-0 text-[12.5px] leading-snug text-[var(--text-secondary)]">{cr}</p>
                                                                        {res?.explanation && (
                                                                            <p className="m-0 mt-1 text-[12.5px] leading-snug text-[var(--text-tertiary)]">{res.explanation}</p>
                                                                        )}
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    ) : (
                                                        <div className="flex flex-col gap-2">
                                                            <div className="flex flex-wrap gap-1.5">
                                                                {skills.slice(0, 14).map((s, i) => (
                                                                    <span
                                                                        key={`${s}-${i}`}
                                                                        className="rounded-md border border-[var(--border-muted)] bg-[var(--surface-primary)] px-2 py-0.5 text-[12px] text-[var(--text-secondary)]"
                                                                    >
                                                                        {s}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                            <div className="text-[12.5px] text-[var(--text-tertiary)]">
                                                                <span className="font-semibold text-[var(--text-secondary)]">Evidence:</span>{' '}
                                                                {(r._evidence || []).join(' · ') || '—'}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </Fragment>
                            );
                        })}
                    </tbody>
                </table>
                {rows.length === 0 && (
                    <div className="p-10 text-center text-[13px] text-[var(--text-muted)]">No candidates yet — run a search.</div>
                )}
            </div>
        </div>
    );
}
